export const MONUMENT_ASSEMBLY_SCHEMA_VERSION = 10
export const MONUMENT_PEDESTAL_WIDTHS_MM = [500, 600, 700, 800, 1000, 1200, 1400]
export const MONUMENT_PEDESTAL_VARIANTS = ['flat', 'bevel']
export const MONUMENT_PLOT_MODES = ['none', 'flowerbed', 'ledger', 'grass']
export const MONUMENT_GRANITE_SOURCE = Object.freeze({
    PARENT: 'parent',
    CUSTOM: 'custom',
})


export const MONUMENT_TILE_LAYOUTS = Object.freeze({
    grid300: {
        id: 'grid300',
        label: 'Плитка 300×300 — сплошная раскладка',
        pieceWidth: 300,
        pieceLength: 300,
        coverage: 'full',
        porcelainAllowed: true,
    },
    frame300: {
        id: 'frame300',
        label: 'Плитка 300×300 — рамка',
        pieceWidth: 300,
        pieceLength: 300,
        coverage: 'frame',
        porcelainAllowed: true,
    },
    slab900x300: {
        id: 'slab900x300',
        label: 'Гранит 900×300 — сплошная, вертикальные плиты',
        pieceWidth: 900,
        pieceLength: 300,
        renderPieceWidth: 300,
        renderPieceLength: 900,
        coverage: 'full-fixed',
        porcelainAllowed: false,
    },
    frame900x300: {
        id: 'frame900x300',
        label: 'Гранит 600×300 — рамка, 6 плит',
        pieceWidth: 600,
        pieceLength: 300,
        coverage: 'frame-reuse-6',
        fixedQuantity: 6,
        porcelainAllowed: false,
    },
    frame600x150: {
        id: 'frame600x150',
        label: 'Гранит: верх/низ 900×300, бока 600×150',
        pieceWidth: 600,
        pieceLength: 150,
        capWidth: 900,
        capLength: 300,
        coverage: 'mixed-frame',
        fixedQuantity: 6,
        porcelainAllowed: false,
    },
    frame600x200: {
        id: 'frame600x200',
        label: 'Гранит: верх/низ 900×300, бока 600×200',
        pieceWidth: 600,
        pieceLength: 200,
        capWidth: 900,
        capLength: 300,
        coverage: 'mixed-frame',
        fixedQuantity: 6,
        porcelainAllowed: false,
    },
})

export const MONUMENT_TILE_LAYOUT_IDS = Object.freeze(Object.keys(MONUMENT_TILE_LAYOUTS))
export const MONUMENT_TILE_MATERIAL_TYPES = Object.freeze(['porcelain', 'granite'])
export const MONUMENT_PORCELAIN_TILE_PRICE_RUB = 450
export const MONUMENT_BEDDING_PRICE_PER_SQUARE_METER_RUB = 5000
export const MONUMENT_PEDESTAL_FIXED_PRICE_RUB = 10000
export const MONUMENT_LONG_CURB_UNIT_PRICE_RUB = 4000
export const MONUMENT_SHORT_CURB_FIXED_PRICE_RUB = 2000


export const MONUMENT_ACCESSORY_PREFIX = 'monument-accessory::'

export const MONUMENT_ASSEMBLY_MATERIAL_PRESETS = Object.freeze({
    blackGranite: { label: 'Чёрный гранит', color: '#202226', roughness: 0.3, metalness: 0.08 },
    grayGranite: { label: 'Серый гранит', color: '#777b80', roughness: 0.42, metalness: 0.04 },
    redGranite: { label: 'Красный гранит', color: '#6f3434', roughness: 0.38, metalness: 0.05 },
    greenGranite: { label: 'Зелёный гранит', color: '#36564b', roughness: 0.4, metalness: 0.04 },
    concrete: { label: 'Бетон', color: '#8b8f94', roughness: 0.94, metalness: 0 },
    grass: { label: 'Трава', color: '#46784d', roughness: 1, metalness: 0 },
    porcelain: { label: 'Керамогранит', color: '#d8d6cf', roughness: 0.62, metalness: 0.01 },
    gravel: { label: 'Подсыпка', color: '#8b8175', roughness: 1, metalness: 0 },
    custom: { label: 'Свой цвет', color: '#55585d', roughness: 0.48, metalness: 0.03 },
})

export const createMonumentAccessorySceneId = (parentModelId, role, quantityIndex = 1) => {
    const parent = encodeURIComponent(String(parentModelId || '').trim())
    const safeRole = encodeURIComponent(String(role || '').trim())
    const index = Math.max(1, Math.round(Number(quantityIndex) || 1))

    return `${MONUMENT_ACCESSORY_PREFIX}${parent}::${safeRole}::${index}`
}

export const parseMonumentAccessorySceneId = (sceneId) => {
    const value = String(sceneId || '')
    if (!value.startsWith(MONUMENT_ACCESSORY_PREFIX)) return null

    const payload = value.slice(MONUMENT_ACCESSORY_PREFIX.length)
    const parts = payload.split('::')
    if (parts.length < 3) return null

    try {
        return {
            sceneId: value,
            parentModelId: decodeURIComponent(parts[0] || ''),
            role: decodeURIComponent(parts[1] || ''),
            quantityIndex: Math.max(1, Math.round(Number(parts[2]) || 1)),
        }
    } catch (_error) {
        return null
    }
}

export const isMonumentAccessorySceneId = (sceneId) => !!parseMonumentAccessorySceneId(sceneId)

export const getMonumentAccessoryParentId = (sceneId) => (
    parseMonumentAccessorySceneId(sceneId)?.parentModelId || null
)

export const isMonumentAssemblyCategoryAllowed = (category) => (
    category?.surfaceEditorEnabled === true
)

export const MONUMENT_ASSEMBLY_PRODUCT_ROLES = Object.freeze({
    pedestal: 'monument-pedestal',
    foundation: 'monument-foundation',
    longCurb: 'monument-flowerbed-long-curb',
    shortCurb: 'monument-flowerbed-short-curb',
    ledger: 'monument-ledger',
    grass: 'monument-grass',
    tile: 'monument-tile',
    bedding: 'monument-bedding',
})

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const numberOr = (value, fallback) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

const normalizePositiveMm = (value, fallback, min = 10, max = 10000) => (
    Math.round(clamp(numberOr(value, fallback), min, max))
)


const normalizeNonNegativeMoney = (value, fallback = 0) => (
    Math.max(0, Math.round(numberOr(value, fallback) * 100) / 100)
)

const normalizeSignedMm = (value, fallback = 0, limit = 5000) => (
    Math.round(clamp(numberOr(value, fallback), -Math.abs(limit), Math.abs(limit)))
)

const normalizeRotationRadians = (value) => {
    const number = Number(value)
    if (!Number.isFinite(number)) return 0

    const twoPi = Math.PI * 2
    let normalized = ((number + Math.PI) % twoPi + twoPi) % twoPi - Math.PI

    // Для ровно 180° сохраняем +180°, а не -180°: так значение стабильнее
    // и понятнее в UI. Полные обороты 360°/720° становятся 0°.
    if (Math.abs(normalized + Math.PI) < 1e-10) normalized = Math.PI
    if (Math.abs(normalized) < 1e-10) normalized = 0

    return normalized
}

export const normalizeMonumentRotation = (value = [0, 0, 0]) => {
    const source = Array.isArray(value)
        ? value
        : [value?.x, value?.y, value?.z]

    return [0, 1, 2].map((index) => normalizeRotationRadians(source?.[index]))
}

export const normalizeMonumentRotationTarget = (value) => (
    String(value || '').trim().toLowerCase() === 'assembly' ? 'assembly' : 'monument'
)

const normalizeTranslationMeters = (value) => {
    const number = Number(value)
    if (!Number.isFinite(number)) return 0

    const clamped = clamp(number, -50, 50)
    const rounded = Math.round(clamped * 10000) / 10000
    return Object.is(rounded, -0) ? 0 : rounded
}

export const normalizeMonumentTranslation = (value = [0, 0, 0]) => {
    const source = Array.isArray(value)
        ? value
        : [value?.x, value?.y, value?.z]

    return [0, 1, 2].map((index) => normalizeTranslationMeters(source?.[index]))
}

export const normalizeMonumentTranslationTarget = (value) => (
    String(value || '').trim().toLowerCase() === 'assembly' ? 'assembly' : 'monument'
)

const normalizePedestalHeightMm = (value, fallback = 150) => {
    const height = normalizePositiveMm(value, fallback, 30, 1000)
    return Math.abs(height - 120) <= Math.abs(height - 150) ? 120 : 150
}

const getTileLayout = (layoutId) => (
    MONUMENT_TILE_LAYOUTS[String(layoutId || '')] || MONUMENT_TILE_LAYOUTS.grid300
)

const getBestFullTileCount = (width, length, pieceWidth, pieceLength) => {
    const direct = Math.ceil(width / pieceWidth) * Math.ceil(length / pieceLength)
    const rotated = Math.ceil(width / pieceLength) * Math.ceil(length / pieceWidth)

    return Math.min(direct, rotated)
}

const calculateTileMetrics = ({
    enabled,
    layout,
    materialType,
    width,
    length,
    pricePerPiece,
} = {}) => {
    const definition = getTileLayout(layout)
    const safeWidth = normalizePositiveMm(width, 900, 100, 10000)
    const safeLength = normalizePositiveMm(length, 1800, 100, 12000)
    const shortSide = Math.min(definition.pieceWidth, definition.pieceLength)
    const longSide = Math.max(definition.pieceWidth, definition.pieceLength)
    let quantity = 0
    let coveredAreaMm2 = 0

    if (enabled) {
        if (definition.coverage === 'frame' || definition.coverage === 'frame-reuse-6') {
            const innerWidth = Math.max(0, safeWidth - shortSide * 2)
            const innerLength = Math.max(0, safeLength - shortSide * 2)
            const topBottomCount = Math.ceil(safeWidth / longSide) * 2
            const sideCount = innerLength > 0
                ? Math.ceil(innerLength / longSide) * 2
                : 0

            quantity = definition.fixedQuantity
                ? Math.max(1, Number(definition.fixedQuantity) || 1)
                : Math.max(1, topBottomCount + sideCount)
            coveredAreaMm2 = Math.max(
                0,
                safeWidth * safeLength - innerWidth * innerLength
            )
        } else if (definition.coverage === 'mixed-frame') {
            const capLength = Math.min(
                Math.max(1, Number(definition.capLength) || 300),
                safeLength / 2
            )
            const sideWidth = Math.min(shortSide, safeWidth / 2)
            const innerWidth = Math.max(0, safeWidth - sideWidth * 2)
            const innerLength = Math.max(0, safeLength - capLength * 2)

            quantity = Math.max(1, Number(definition.fixedQuantity) || 6)
            coveredAreaMm2 = Math.max(
                0,
                safeWidth * safeLength - innerWidth * innerLength
            )
        } else {
            quantity = Math.max(
                1,
                getBestFullTileCount(
                    safeWidth,
                    safeLength,
                    definition.renderPieceWidth || definition.pieceWidth,
                    definition.renderPieceLength || definition.pieceLength
                )
            )
            coveredAreaMm2 = safeWidth * safeLength
        }
    }

    const normalizedMaterialType = (
        materialType === 'porcelain' && definition.porcelainAllowed
            ? 'porcelain'
            : 'granite'
    )
    const unitPrice = normalizedMaterialType === 'porcelain'
        ? MONUMENT_PORCELAIN_TILE_PRICE_RUB
        : normalizeNonNegativeMoney(pricePerPiece, 0)
    const total = quantity * unitPrice

    return {
        enabled: !!enabled,
        layout: definition.id,
        layoutLabel: definition.label,
        coverage: definition.coverage,
        pieceWidth: definition.pieceWidth,
        pieceLength: definition.pieceLength,
        materialType: normalizedMaterialType,
        width: safeWidth,
        length: safeLength,
        quantity,
        coveredAreaSquareMeters: Math.round((coveredAreaMm2 / 1_000_000) * 1000) / 1000,
        unitPrice,
        total: Math.round(total * 100) / 100,
    }
}

const calculateBeddingMetrics = ({
    enabled,
    width,
    length,
    pricePerSquareMeter,
} = {}) => {
    const safeWidth = normalizePositiveMm(width, 2500, 300, 10000)
    const safeLength = normalizePositiveMm(length, 3000, 300, 12000)
    const areaSquareMeters = Math.round((safeWidth * safeLength / 1_000_000) * 1000) / 1000
    const unitPrice = normalizeNonNegativeMoney(
        pricePerSquareMeter,
        MONUMENT_BEDDING_PRICE_PER_SQUARE_METER_RUB
    )

    return {
        enabled: !!enabled,
        width: safeWidth,
        length: safeLength,
        areaSquareMeters,
        unitPrice,
        total: enabled
            ? Math.round(areaSquareMeters * unitPrice * 100) / 100
            : 0,
    }
}

const isTextureAppearance = (value) => (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.hasOwnProperty.call(value, 'url')
)

const cloneMaterialAppearance = (value) => {
    if (isTextureAppearance(value)) {
        return {
            ...value,
            repeat: Array.isArray(value.repeat) ? value.repeat.slice(0, 2) : value.repeat,
            offset: Array.isArray(value.offset) ? value.offset.slice(0, 2) : value.offset,
        }
    }

    if (typeof value === 'string' && value.trim()) return value
    return 'original'
}

export const normalizeMonumentGraniteMaterialKey = (value = '') => {
    const key = String(value || '').trim().toLowerCase()
    return key === 'kapustinsky' ? 'vinga' : key
}

export const getMonumentGraniteKeyFromAppearance = (value) => {
    if (!value) return ''

    if (Array.isArray(value)) {
        for (const item of value) {
            const found = getMonumentGraniteKeyFromAppearance(item)
            if (found) return found
        }
        return ''
    }

    if (value && typeof value === 'object') {
        const direct = normalizeMonumentGraniteMaterialKey(
            value.materialKey || value.blankMaterialKey || value.textureKey
        )
        if (direct && !['parent', 'inherit', 'original'].includes(direct)) return direct

        for (const item of Object.values(value)) {
            const found = getMonumentGraniteKeyFromAppearance(item)
            if (found) return found
        }
    }

    return ''
}

export const isMonumentGraniteInherited = (material = {}) => (
    material?.graniteSource !== MONUMENT_GRANITE_SOURCE.CUSTOM
)

export const getMonumentGraniteOverrideKey = (material = {}) => normalizeMonumentGraniteMaterialKey(
    material?.materialKey ||
    getMonumentGraniteKeyFromAppearance(material?.appearance) ||
    material?.textureKey
)

const hasExplicitLegacyTexture = (value) => {
    if (isTextureAppearance(value)) return true
    if (typeof value !== 'string') return false

    const normalized = value.trim().toLowerCase()
    if (!normalized || normalized === 'original' || normalized === 'original_texture') return false

    return value.includes('/') || /\.(png|jpe?g|webp|gif|avif)(\?.*)?$/i.test(value)
}

const normalizeMaterial = (value, fallbackPreset = 'blackGranite', fallbackColor = '') => {
    const source = value && typeof value === 'object' ? value : {}
    const requestedPreset = String(source.preset || fallbackPreset || 'custom')
    const preset = MONUMENT_ASSEMBLY_MATERIAL_PRESETS[requestedPreset]
        ? requestedPreset
        : 'custom'
    const presetData = MONUMENT_ASSEMBLY_MATERIAL_PRESETS[preset]
    const color = String(source.color || fallbackColor || presetData.color)
    const roughness = clamp(numberOr(source.roughness, presetData.roughness), 0, 1)
    const metalness = clamp(numberOr(source.metalness, presetData.metalness), 0, 1)

    return {
        preset,
        color,
        roughness,
        metalness,
        textureProductId: String(source.textureProductId || ''),
        textureKey: String(source.textureKey || preset),
        appearance: cloneMaterialAppearance(
            source.appearance ??
            source.selectorValue ??
            source.textureValue ??
            'original'
        ),
    }
}

const normalizeGraniteMaterial = (value, fallbackPreset = 'blackGranite', fallbackColor = '') => {
    const source = value && typeof value === 'object' ? value : {}
    const normalized = normalizeMaterial(source, fallbackPreset, fallbackColor)
    const appearance = source.appearance ?? source.selectorValue ?? source.textureValue ?? 'original'
    const explicitSource = source.graniteSource === MONUMENT_GRANITE_SOURCE.CUSTOM
        ? MONUMENT_GRANITE_SOURCE.CUSTOM
        : source.graniteSource === MONUMENT_GRANITE_SOURCE.PARENT
            ? MONUMENT_GRANITE_SOURCE.PARENT
            : ''
    const overrideKey = normalizeMonumentGraniteMaterialKey(
        source.materialKey ||
        getMonumentGraniteKeyFromAppearance(appearance) ||
        (source.textureKey && !MONUMENT_ASSEMBLY_MATERIAL_PRESETS[source.textureKey] ? source.textureKey : '')
    )
    const graniteSource = explicitSource || (
        overrideKey || hasExplicitLegacyTexture(appearance)
            ? MONUMENT_GRANITE_SOURCE.CUSTOM
            : MONUMENT_GRANITE_SOURCE.PARENT
    )

    return {
        ...normalized,
        graniteSource,
        materialKey: graniteSource === MONUMENT_GRANITE_SOURCE.CUSTOM ? overrideKey : '',
        textureKey: graniteSource === MONUMENT_GRANITE_SOURCE.CUSTOM
            ? (overrideKey || normalized.textureKey)
            : 'parent',
        appearance: graniteSource === MONUMENT_GRANITE_SOURCE.PARENT
            ? 'original'
            : normalized.appearance,
    }
}

const normalizeProductRef = (value, {
    role,
    quantity = 1,
    variant = '',
} = {}) => {
    const source = value && typeof value === 'object' ? value : {}

    return {
        role: String(source.role || role || ''),
        productId: String(source.productId || source._id || ''),
        categoryId: String(source.categoryId || ''),
        categorySlug: String(source.categorySlug || ''),
        quantity: Math.max(1, Math.round(numberOr(source.quantity, quantity))),
        variant: String(source.variant || variant || ''),
    }
}

export const getAutoPedestalWidthMm = (monumentWidthMm = 500) => {
    const width = normalizePositiveMm(monumentWidthMm, 500, 1, 10000)

    // Подставка всегда берётся на один стандартный шаг шире памятника:
    // 400 → 500, 500 → 600, 600 → 700, 700 → 800 и т. д.
    return MONUMENT_PEDESTAL_WIDTHS_MM.find((item) => item > width) || MONUMENT_PEDESTAL_WIDTHS_MM[MONUMENT_PEDESTAL_WIDTHS_MM.length - 1]
}

export const normalizePedestalWidthMm = (value, fallback = 600) => {
    const requested = normalizePositiveMm(value, fallback, 1, 10000)

    return MONUMENT_PEDESTAL_WIDTHS_MM.find((item) => item >= requested) || MONUMENT_PEDESTAL_WIDTHS_MM[MONUMENT_PEDESTAL_WIDTHS_MM.length - 1]
}

export const getDefaultMonumentAssembly = (monumentDimensions = {}) => {
    const monumentWidth = normalizePositiveMm(monumentDimensions.width, 500)
    const monumentThickness = normalizePositiveMm(monumentDimensions.thickness, 80)
    const pedestalWidth = getAutoPedestalWidthMm(monumentWidth)

    return {
        schemaVersion: MONUMENT_ASSEMBLY_SCHEMA_VERSION,
        enabled: false,
        plotMode: 'none',
        monumentDimensions: {
            height: normalizePositiveMm(monumentDimensions.height, 1000),
            width: monumentWidth,
            thickness: monumentThickness,
        },
        // Смещается только верхний комплект: памятник, подставка и заполнение.
        // Заливка, плитка и подсыпка остаются на месте.
        upperOffset: {
            x: 0,
            y: 0,
        },
        // Положение и вращение стелы отделены от трансформации всего комплекта.
        // monumentTranslation — локальное смещение только стелы в метрах;
        // positions[path] — положение всего комплекта в сцене.
        // monumentRotation — стела относительно комплекта;
        // assemblyRotation — весь комплект относительно сцены.
        // positions[path] / rotations[path] остаются общей инфраструктурой сцены.
        monumentTranslation: [0, 0, 0],
        translationTarget: 'monument',
        monumentRotation: [0, 0, 0],
        assemblyRotation: [0, 0, 0],
        rotationTarget: 'monument',
        pedestal: {
            enabled: false,
            variant: 'flat',
            sizeMode: 'monument',
            widthMode: 'auto',
            width: pedestalWidth,
            height: 150,
            depthMode: 'standard',
            depth: 200,
            pricePerSquareMeter: 0,
            color: '#202226',
            material: normalizeGraniteMaterial(null, 'blackGranite', '#202226'),
            polishedBevel: false,
            epitaphEnabled: false,
            epitaphText: '',
            textSurfaceId: 'pedestal-front-bevel',
            product: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.pedestal,
                quantity: 1,
                variant: 'flat',
            }),
        },
        bedding: {
            enabled: false,
            width: 2500,
            length: 3000,
            height: 50,
            pricePerSquareMeter: MONUMENT_BEDDING_PRICE_PER_SQUARE_METER_RUB,
            color: '#8b8175',
            material: normalizeMaterial(null, 'gravel', '#8b8175'),
            product: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.bedding,
                quantity: 1,
            }),
        },
        foundation: {
            enabled: false,
            sizeMode: 'standard',
            autoWidth: false,
            width: 900,
            depth: 1800,
            height: 100,
            color: '#8b8f94',
            material: normalizeMaterial(null, 'concrete', '#8b8f94'),
            product: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.foundation,
                quantity: 1,
            }),
        },
        tile: {
            enabled: false,
            autoSize: true,
            width: 900,
            length: 1800,
            height: 10,
            layout: 'grid300',
            materialType: 'porcelain',
            pricePerPiece: MONUMENT_PORCELAIN_TILE_PRICE_RUB,
            color: '#d8d6cf',
            material: normalizeMaterial(null, 'porcelain', '#d8d6cf'),
            product: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.tile,
                quantity: 18,
                variant: 'grid300',
            }),
        },
        flowerbed: {
            enabled: false,
            autoOuterWidth: true,
            outerWidth: pedestalWidth,
            length: 1000,
            curbWidth: 80,
            curbHeight: 80,
            color: '#202226',
            longCurbMaterial: normalizeGraniteMaterial(null, 'blackGranite', '#202226'),
            shortCurbMaterial: normalizeGraniteMaterial(null, 'blackGranite', '#202226'),
            longCurbProduct: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb,
                quantity: 2,
            }),
            shortCurbProduct: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb,
                quantity: 1,
            }),
        },
        ledger: {
            enabled: false,
            style: 'flat',
            autoWidth: true,
            width: pedestalWidth,
            length: 1200,
            height: 80,
            color: '#202226',
            material: normalizeGraniteMaterial(null, 'blackGranite', '#202226'),
            epitaphEnabled: false,
            epitaphText: '',
            textSurfaceId: 'ledger-top',
            product: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.ledger,
                quantity: 1,
                variant: 'flat',
            }),
        },
        grass: {
            enabled: false,
            autoWidth: true,
            width: pedestalWidth,
            length: 1200,
            height: 20,
            color: '#46784d',
            material: normalizeMaterial(null, 'grass', '#46784d'),
            product: normalizeProductRef(null, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.grass,
                quantity: 1,
            }),
        },
    }
}

export const getDefaultShowcaseMonumentAssembly = (monumentDimensions = {}) => {
    const defaults = getDefaultMonumentAssembly(monumentDimensions)

    return normalizeMonumentAssembly({
        ...defaults,
        enabled: true,
        plotMode: 'flowerbed',
        pedestal: {
            ...defaults.pedestal,
            enabled: true,
            height: 150,
            sizeMode: 'monument',
            depthMode: 'standard',
            depth: 200,
        },
        foundation: {
            ...defaults.foundation,
            enabled: true,
            sizeMode: 'standard',
            width: 900,
            depth: 1800,
            height: 100,
        },
        tile: {
            ...defaults.tile,
            enabled: false,
        },
        flowerbed: {
            ...defaults.flowerbed,
            enabled: true,
        },
    }, monumentDimensions)
}

export const normalizeMonumentAssembly = (value = {}, monumentDimensions = {}) => {
    const defaults = getDefaultMonumentAssembly(monumentDimensions)
    const source = value && typeof value === 'object' ? value : {}
    const sourceSchemaVersion = Math.max(1, Number(source.schemaVersion) || 1)
    const sourcePedestal = source.pedestal && typeof source.pedestal === 'object' ? source.pedestal : {}
    const sourceBedding = source.bedding && typeof source.bedding === 'object' ? source.bedding : {}
    const sourceFoundation = source.foundation && typeof source.foundation === 'object' ? source.foundation : {}
    const sourceTile = source.tile && typeof source.tile === 'object' ? source.tile : {}
    const sourceFlowerbed = source.flowerbed && typeof source.flowerbed === 'object' ? source.flowerbed : {}
    const sourceUpperOffset = source.upperOffset && typeof source.upperOffset === 'object'
        ? source.upperOffset
        : (source.assemblyOffset && typeof source.assemblyOffset === 'object' ? source.assemblyOffset : {})
    const sourceLedger = source.ledger && typeof source.ledger === 'object' ? source.ledger : {}
    const sourceGrass = source.grass && typeof source.grass === 'object' ? source.grass : {}
    const rememberedDimensions = source.monumentDimensions && typeof source.monumentDimensions === 'object'
        ? source.monumentDimensions
        : {}
    const resolvedMonumentDimensions = {
        height: normalizePositiveMm(monumentDimensions.height, rememberedDimensions.height || defaults.monumentDimensions.height),
        width: normalizePositiveMm(monumentDimensions.width, rememberedDimensions.width || defaults.monumentDimensions.width),
        thickness: normalizePositiveMm(monumentDimensions.thickness, rememberedDimensions.thickness || defaults.monumentDimensions.thickness),
    }

    const beddingEnabled = !!sourceBedding.enabled
    const beddingWidth = normalizePositiveMm(sourceBedding.width, defaults.bedding.width, 300, 10000)
    const beddingLength = normalizePositiveMm(
        sourceBedding.length ?? sourceBedding.depth,
        defaults.bedding.length,
        300,
        12000
    )
    const beddingHeightRaw = normalizePositiveMm(sourceBedding.height, defaults.bedding.height, 20, 200)
    const beddingHeight = Math.abs(beddingHeightRaw - 100) < Math.abs(beddingHeightRaw - 50)
        ? 100
        : 50
    const beddingMaterial = normalizeMaterial(
        sourceBedding.material,
        'gravel',
        sourceBedding.color || defaults.bedding.color
    )

    const autoPedestalWidth = getAutoPedestalWidthMm(resolvedMonumentDimensions.width)

    const hasFoundationSizeMode = Object.prototype.hasOwnProperty.call(sourceFoundation, 'sizeMode')
    const hasFoundationAutoWidth = Object.prototype.hasOwnProperty.call(sourceFoundation, 'autoWidth')
    const foundationSizeMode = ['standard', 'monument', 'bedding', 'manual'].includes(sourceFoundation.sizeMode)
        ? sourceFoundation.sizeMode
        : hasFoundationAutoWidth
            ? (sourceFoundation.autoWidth === true ? 'monument' : 'manual')
            : hasFoundationSizeMode
                ? 'standard'
                : defaults.foundation.sizeMode
    let foundationWidth = normalizePositiveMm(sourceFoundation.width, defaults.foundation.width, 300, 10000)
    let foundationDepth = normalizePositiveMm(
        sourceFoundation.depth ?? sourceFoundation.length,
        defaults.foundation.depth,
        300,
        12000
    )

    if (foundationSizeMode === 'standard') {
        foundationWidth = 900
        foundationDepth = 1800
    } else if (foundationSizeMode === 'monument') {
        foundationWidth = Math.max(900, autoPedestalWidth + 400)
        foundationDepth = 1800
    } else if (foundationSizeMode === 'bedding') {
        foundationWidth = beddingWidth
        foundationDepth = beddingLength
    }

    const pedestalSizeMode = sourcePedestal.sizeMode === 'manual' || sourcePedestal.widthMode === 'manual'
        ? 'manual'
        : 'monument'
    const pedestalWidth = pedestalSizeMode === 'monument'
        ? autoPedestalWidth
        : normalizePedestalWidthMm(sourcePedestal.width, autoPedestalWidth)
    const pedestalDepth = 200

    const pedestalVariant = MONUMENT_PEDESTAL_VARIANTS.includes(sourcePedestal.variant)
        ? sourcePedestal.variant
        : 'flat'
    const pedestalMaterial = normalizeGraniteMaterial(
        sourcePedestal.material,
        'blackGranite',
        sourcePedestal.color || defaults.pedestal.color
    )

    const tileLayout = getTileLayout(sourceTile.layout || defaults.tile.layout)
    const requestedTileMaterialType = MONUMENT_TILE_MATERIAL_TYPES.includes(sourceTile.materialType)
        ? sourceTile.materialType
        : defaults.tile.materialType
    const tileMaterialType = (
        requestedTileMaterialType === 'porcelain' && tileLayout.porcelainAllowed
            ? 'porcelain'
            : 'granite'
    )
    const tileAutoSize = sourceTile.autoSize !== false
    const tileWidth = tileAutoSize
        ? foundationWidth
        : normalizePositiveMm(sourceTile.width, foundationWidth, 100, 10000)
    const tileLength = tileAutoSize
        ? foundationDepth
        : normalizePositiveMm(sourceTile.length ?? sourceTile.depth, foundationDepth, 100, 12000)
    const sourceTileHeight = numberOr(sourceTile.height, 10)
    const migratedTileHeight = sourceSchemaVersion < 5 && (sourceTileHeight === 20 || sourceTileHeight === 30)
        ? 10
        : sourceTileHeight
    const tileHeight = normalizePositiveMm(
        migratedTileHeight,
        10,
        5,
        100
    )
    const tileMaterialWasForcedToGranite = (
        requestedTileMaterialType === 'porcelain' &&
        !tileLayout.porcelainAllowed
    )
    const tilePricePerPiece = tileMaterialType === 'porcelain'
        ? MONUMENT_PORCELAIN_TILE_PRICE_RUB
        : tileMaterialWasForcedToGranite
            ? 0
            : normalizeNonNegativeMoney(sourceTile.pricePerPiece, 0)
    const tileMaterial = tileMaterialType === 'granite'
        ? normalizeGraniteMaterial(
            sourceTile.material,
            'grayGranite',
            sourceTile.color || '#777b80'
        )
        : normalizeMaterial(
            sourceTile.material,
            'porcelain',
            sourceTile.color || '#d8d6cf'
        )
    const tileMetrics = calculateTileMetrics({
        enabled: !!sourceTile.enabled,
        layout: tileLayout.id,
        materialType: tileMaterialType,
        width: tileWidth,
        length: tileLength,
        pricePerPiece: tilePricePerPiece,
    })

    const curbWidth = normalizePositiveMm(sourceFlowerbed.curbWidth, defaults.flowerbed.curbWidth, 30, 500)
    const autoOuterWidth = sourceFlowerbed.autoOuterWidth !== false && sourceFlowerbed.autoWidth !== false
    const migratedManualOuterWidth = sourceFlowerbed.outerWidth || (
        sourceSchemaVersion < 3 && sourceFlowerbed.innerWidth
            ? Number(sourceFlowerbed.innerWidth) + curbWidth * 2
            : null
    )
    const flowerbedOuterWidth = autoOuterWidth
        ? pedestalWidth
        : normalizePositiveMm(migratedManualOuterWidth, pedestalWidth, curbWidth * 2 + 100, 10000)
    const ledgerWidth = sourceLedger.autoWidth === false
        ? normalizePositiveMm(sourceLedger.width, pedestalWidth, 250, 10000)
        : pedestalWidth
    const grassWidth = sourceGrass.autoWidth === false
        ? normalizePositiveMm(sourceGrass.width, pedestalWidth, 250, 10000)
        : pedestalWidth
    const explicitPlotMode = MONUMENT_PLOT_MODES.includes(source.plotMode)
        ? source.plotMode
        : null
    const plotMode = explicitPlotMode || (
        sourceLedger.enabled
            ? 'ledger'
            : sourceGrass.enabled
                ? 'grass'
                : sourceFlowerbed.enabled
                    ? 'flowerbed'
                    : 'none'
    )

    const pedestalEnabled = !!sourcePedestal.enabled
    const foundationEnabled = !!sourceFoundation.enabled
    const tileEnabled = !!sourceTile.enabled
    const flowerbedEnabled = plotMode === 'flowerbed'
    const ledgerEnabled = plotMode === 'ledger'
    const grassEnabled = plotMode === 'grass'
    const enabled = !!(
        source.enabled ||
        beddingEnabled ||
        pedestalEnabled ||
        foundationEnabled ||
        tileEnabled ||
        plotMode !== 'none'
    )
    const foundationMaterial = normalizeMaterial(
        sourceFoundation.material,
        'concrete',
        sourceFoundation.color || defaults.foundation.color
    )
    const flowerbedLongMaterial = normalizeGraniteMaterial(
        sourceFlowerbed.longCurbMaterial,
        'blackGranite',
        sourceFlowerbed.color || defaults.flowerbed.color
    )
    const flowerbedShortMaterial = normalizeGraniteMaterial(
        sourceFlowerbed.shortCurbMaterial,
        'blackGranite',
        sourceFlowerbed.color || defaults.flowerbed.color
    )
    const ledgerMaterial = normalizeGraniteMaterial(
        sourceLedger.material,
        'blackGranite',
        sourceLedger.color || defaults.ledger.color
    )
    const grassMaterial = normalizeMaterial(
        sourceGrass.material,
        'grass',
        sourceGrass.color || defaults.grass.color
    )
    const upperOffset = {
        x: normalizeSignedMm(sourceUpperOffset.x, 0, 5000),
        y: normalizeSignedMm(sourceUpperOffset.y, 0, 5000),
    }
    const monumentTranslation = normalizeMonumentTranslation(
        source.monumentTranslation ?? source.monumentLocalTranslation ?? defaults.monumentTranslation
    )
    const translationTarget = normalizeMonumentTranslationTarget(
        source.translationTarget ?? source.monumentTranslationTarget ?? defaults.translationTarget
    )
    const monumentRotation = normalizeMonumentRotation(
        source.monumentRotation ?? source.monumentLocalRotation ?? defaults.monumentRotation
    )
    const hasExplicitAssemblyRotation = Object.prototype.hasOwnProperty.call(source, 'assemblyRotation')
        || Object.prototype.hasOwnProperty.call(source, 'monumentAssemblyRotation')
    const assemblyRotation = hasExplicitAssemblyRotation
        ? normalizeMonumentRotation(source.assemblyRotation ?? source.monumentAssemblyRotation)
        : (sourceSchemaVersion >= 9 ? normalizeMonumentRotation(defaults.assemblyRotation) : null)
    const rotationTarget = normalizeMonumentRotationTarget(
        source.rotationTarget ?? source.monumentRotationTarget ?? defaults.rotationTarget
    )

    return {
        schemaVersion: MONUMENT_ASSEMBLY_SCHEMA_VERSION,
        enabled,
        plotMode,
        monumentDimensions: resolvedMonumentDimensions,
        upperOffset,
        monumentTranslation,
        translationTarget,
        monumentRotation,
        assemblyRotation,
        rotationTarget,
        pedestal: {
            enabled: pedestalEnabled,
            variant: pedestalVariant,
            sizeMode: pedestalSizeMode,
            widthMode: pedestalSizeMode === 'monument' ? 'auto' : 'manual',
            width: pedestalWidth,
            height: normalizePedestalHeightMm(sourcePedestal.height, defaults.pedestal.height),
            depthMode: pedestalSizeMode === 'monument' ? 'standard' : 'manual',
            depth: pedestalDepth,
            pricePerSquareMeter: normalizeNonNegativeMoney(sourcePedestal.pricePerSquareMeter, 0),
            material: pedestalMaterial,
            color: pedestalMaterial.color,
            polishedBevel: pedestalVariant === 'bevel' && sourcePedestal.polishedBevel !== false,
            epitaphEnabled: pedestalVariant === 'bevel' && !!sourcePedestal.epitaphEnabled,
            epitaphText: String(sourcePedestal.epitaphText || ''),
            textSurfaceId: String(sourcePedestal.textSurfaceId || defaults.pedestal.textSurfaceId),
            product: normalizeProductRef(sourcePedestal.product, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.pedestal,
                quantity: 1,
                variant: pedestalVariant,
            }),
        },
        bedding: {
            enabled: beddingEnabled,
            width: beddingWidth,
            length: beddingLength,
            height: beddingHeight,
            pricePerSquareMeter: normalizeNonNegativeMoney(
                sourceBedding.pricePerSquareMeter,
                MONUMENT_BEDDING_PRICE_PER_SQUARE_METER_RUB
            ),
            material: beddingMaterial,
            color: beddingMaterial.color,
            product: normalizeProductRef(sourceBedding.product, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.bedding,
                quantity: 1,
            }),
        },
        foundation: {
            enabled: foundationEnabled,
            sizeMode: foundationSizeMode,
            autoWidth: foundationSizeMode === 'monument',
            width: foundationWidth,
            depth: foundationDepth,
            height: normalizePositiveMm(sourceFoundation.height, defaults.foundation.height, 20, 500),
            material: foundationMaterial,
            color: foundationMaterial.color,
            product: normalizeProductRef(sourceFoundation.product, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.foundation,
                quantity: 1,
            }),
        },
        tile: {
            enabled: tileEnabled,
            autoSize: tileAutoSize,
            width: tileWidth,
            length: tileLength,
            height: tileHeight,
            layout: tileLayout.id,
            materialType: tileMetrics.materialType,
            pricePerPiece: tilePricePerPiece,
            material: tileMaterial,
            color: tileMaterial.color,
            product: {
                ...normalizeProductRef(sourceTile.product, {
                    role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.tile,
                    quantity: Math.max(1, tileMetrics.quantity || 1),
                    variant: tileLayout.id,
                }),
                quantity: Math.max(1, tileMetrics.quantity || 1),
                variant: tileLayout.id,
            },
        },
        flowerbed: {
            enabled: flowerbedEnabled,
            autoOuterWidth,
            outerWidth: flowerbedOuterWidth,
            innerWidth: Math.max(100, flowerbedOuterWidth - curbWidth * 2),
            // Два длинных поребрика всегда по 1000 мм.
            length: 1000,
            curbWidth,
            curbHeight: normalizePositiveMm(sourceFlowerbed.curbHeight, defaults.flowerbed.curbHeight, 30, 500),
            longCurbMaterial: flowerbedLongMaterial,
            shortCurbMaterial: flowerbedShortMaterial,
            color: flowerbedLongMaterial.color,
            longCurbProduct: normalizeProductRef(sourceFlowerbed.longCurbProduct, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb,
                quantity: 2,
            }),
            shortCurbProduct: normalizeProductRef(sourceFlowerbed.shortCurbProduct, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb,
                quantity: 1,
            }),
        },
        ledger: {
            enabled: ledgerEnabled,
            style: sourceLedger.style === 'shaped' ? 'shaped' : 'flat',
            autoWidth: sourceLedger.autoWidth !== false,
            width: ledgerWidth,
            length: normalizePositiveMm(sourceLedger.length, defaults.ledger.length, 300, 5000),
            height: normalizePositiveMm(sourceLedger.height, defaults.ledger.height, 20, 500),
            material: ledgerMaterial,
            color: ledgerMaterial.color,
            epitaphEnabled: !!sourceLedger.epitaphEnabled,
            epitaphText: String(sourceLedger.epitaphText || ''),
            textSurfaceId: String(sourceLedger.textSurfaceId || defaults.ledger.textSurfaceId),
            product: normalizeProductRef(sourceLedger.product, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.ledger,
                quantity: 1,
                variant: sourceLedger.style === 'shaped' ? 'shaped' : 'flat',
            }),
        },
        grass: {
            enabled: grassEnabled,
            autoWidth: sourceGrass.autoWidth !== false,
            width: grassWidth,
            length: normalizePositiveMm(sourceGrass.length, defaults.grass.length, 300, 5000),
            height: normalizePositiveMm(sourceGrass.height, defaults.grass.height, 5, 100),
            material: grassMaterial,
            color: grassMaterial.color,
            product: normalizeProductRef(sourceGrass.product, {
                role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.grass,
                quantity: 1,
            }),
        },
    }
}

export const patchMonumentAssembly = (current, patch = {}, monumentDimensions = {}) => {
    const base = normalizeMonumentAssembly(current, monumentDimensions)
    const mergeMaterial = (currentMaterial, nextMaterial) => ({
        ...currentMaterial,
        ...(nextMaterial || {}),
    })
    const next = {
        ...base,
        ...patch,
        pedestal: {
            ...base.pedestal,
            ...(patch.pedestal || {}),
            material: mergeMaterial(base.pedestal.material, patch.pedestal?.material),
        },
        bedding: {
            ...base.bedding,
            ...(patch.bedding || {}),
            material: mergeMaterial(base.bedding.material, patch.bedding?.material),
        },
        foundation: {
            ...base.foundation,
            ...(patch.foundation || {}),
            material: mergeMaterial(base.foundation.material, patch.foundation?.material),
        },
        tile: {
            ...base.tile,
            ...(patch.tile || {}),
            material: mergeMaterial(base.tile.material, patch.tile?.material),
        },
        flowerbed: {
            ...base.flowerbed,
            ...(patch.flowerbed || {}),
            longCurbMaterial: mergeMaterial(
                base.flowerbed.longCurbMaterial,
                patch.flowerbed?.longCurbMaterial
            ),
            shortCurbMaterial: mergeMaterial(
                base.flowerbed.shortCurbMaterial,
                patch.flowerbed?.shortCurbMaterial
            ),
        },
        ledger: {
            ...base.ledger,
            ...(patch.ledger || {}),
            material: mergeMaterial(base.ledger.material, patch.ledger?.material),
        },
        grass: {
            ...base.grass,
            ...(patch.grass || {}),
            material: mergeMaterial(base.grass.material, patch.grass?.material),
        },
        monumentDimensions: {
            ...base.monumentDimensions,
            ...(patch.monumentDimensions || monumentDimensions || {}),
        },
        upperOffset: {
            ...base.upperOffset,
            ...(patch.upperOffset || {}),
        },
        monumentTranslation: Object.prototype.hasOwnProperty.call(patch, 'monumentTranslation')
            ? normalizeMonumentTranslation(patch.monumentTranslation)
            : base.monumentTranslation,
        translationTarget: Object.prototype.hasOwnProperty.call(patch, 'translationTarget')
            ? normalizeMonumentTranslationTarget(patch.translationTarget)
            : base.translationTarget,
        monumentRotation: Object.prototype.hasOwnProperty.call(patch, 'monumentRotation')
            ? normalizeMonumentRotation(patch.monumentRotation)
            : base.monumentRotation,
        assemblyRotation: Object.prototype.hasOwnProperty.call(patch, 'assemblyRotation')
            ? normalizeMonumentRotation(patch.assemblyRotation)
            : base.assemblyRotation,
        rotationTarget: Object.prototype.hasOwnProperty.call(patch, 'rotationTarget')
            ? normalizeMonumentRotationTarget(patch.rotationTarget)
            : base.rotationTarget,
    }

    if (Object.prototype.hasOwnProperty.call(patch, 'plotMode')) {
        next.flowerbed.enabled = patch.plotMode === 'flowerbed'
        next.ledger.enabled = patch.plotMode === 'ledger'
        next.grass.enabled = patch.plotMode === 'grass'
    }

    return normalizeMonumentAssembly(next, monumentDimensions)
}

export const getMonumentTileMetrics = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)

    return calculateTileMetrics({
        enabled: normalized.tile.enabled,
        layout: normalized.tile.layout,
        materialType: normalized.tile.materialType,
        width: normalized.tile.width,
        length: normalized.tile.length,
        pricePerPiece: normalized.tile.pricePerPiece,
    })
}

export const getMonumentBeddingMetrics = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)

    return calculateBeddingMetrics({
        enabled: normalized.bedding.enabled,
        width: normalized.bedding.width,
        length: normalized.bedding.length,
        pricePerSquareMeter: normalized.bedding.pricePerSquareMeter,
    })
}

export const getMonumentAssemblyKnownPrice = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)
    const tile = getMonumentTileMetrics(normalized, monumentDimensions)
    const bedding = getMonumentBeddingMetrics(normalized, monumentDimensions)

    const pedestalTotal = normalized.pedestal.enabled
        ? MONUMENT_PEDESTAL_FIXED_PRICE_RUB
        : 0
    const longCurbQuantity = normalized.plotMode === 'flowerbed' ? 2 : 0
    const longCurbTotal = longCurbQuantity * MONUMENT_LONG_CURB_UNIT_PRICE_RUB
    const shortCurbQuantity = normalized.plotMode === 'flowerbed' ? 1 : 0
    const shortCurbTotal = shortCurbQuantity * MONUMENT_SHORT_CURB_FIXED_PRICE_RUB
    const flowerbedTotal = longCurbTotal + shortCurbTotal
    const total = Math.round((
        tile.total +
        bedding.total +
        pedestalTotal +
        flowerbedTotal
    ) * 100) / 100

    return {
        tile,
        bedding,
        pedestal: {
            enabled: normalized.pedestal.enabled,
            calcMode: 'piece',
            quantity: normalized.pedestal.enabled ? 1 : 0,
            unitPrice: MONUMENT_PEDESTAL_FIXED_PRICE_RUB,
            total: pedestalTotal,
        },
        flowerbed: {
            enabled: normalized.plotMode === 'flowerbed',
            longCurb: {
                quantity: longCurbQuantity,
                unitPrice: MONUMENT_LONG_CURB_UNIT_PRICE_RUB,
                total: longCurbTotal,
            },
            shortCurb: {
                quantity: shortCurbQuantity,
                unitPrice: MONUMENT_SHORT_CURB_FIXED_PRICE_RUB,
                total: shortCurbTotal,
            },
            total: flowerbedTotal,
        },
        total,
    }
}

export const getMonumentAssemblyLiftMeters = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)
    const bedding = normalized.bedding.enabled ? normalized.bedding.height : 0
    const foundation = normalized.foundation.enabled ? normalized.foundation.height : 0
    const tile = normalized.tile.enabled ? normalized.tile.height : 0
    const pedestal = normalized.pedestal.enabled ? normalized.pedestal.height : 0

    return (bedding + foundation + tile + pedestal) / 1000
}

export const getMonumentAssemblyUpperPlacementMm = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)
    const manualX = normalizeSignedMm(normalized.upperOffset?.x, 0, 5000)
    const manualY = normalizeSignedMm(normalized.upperOffset?.y, 0, 5000)
    const forwardLength = normalized.plotMode === 'flowerbed'
        ? normalized.flowerbed.length + normalized.flowerbed.curbWidth
        : normalized.plotMode === 'ledger'
            ? normalized.ledger.length
            : normalized.plotMode === 'grass'
                ? normalized.grass.length
                : 0
    const autoCenterY = forwardLength / 2

    return {
        x: manualX,
        y: Math.round((autoCenterY + manualY) * 1000) / 1000,
        manualX,
        manualY,
        autoCenterY,
        forwardLength,
    }
}

export const getMonumentAssemblyUpperOffsetMeters = (assembly = {}, monumentDimensions = {}) => {
    const placement = getMonumentAssemblyUpperPlacementMm(assembly, monumentDimensions)

    return [placement.x / 1000, placement.y / 1000]
}

export const getMonumentAssemblyCatalogLines = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)
    const pricing = getMonumentAssemblyKnownPrice(normalized, monumentDimensions)
    const lines = []

    const pushLine = ({
        ref,
        role,
        title,
        quantity,
        dimensions,
        variant = '',
        pricingInfo = null,
        note = '',
    }) => {
        const product = normalizeProductRef(ref, { role, quantity, variant })

        lines.push({
            role,
            title,
            productId: product.productId,
            categoryId: product.categoryId,
            categorySlug: product.categorySlug,
            quantity: Math.max(1, Number(quantity || product.quantity) || 1),
            variant: variant || product.variant || '',
            dimensions,
            pricing: pricingInfo,
            note,
            pendingCatalogBinding: !product.productId,
        })
    }

    if (normalized.bedding.enabled) {
        pushLine({
            ref: normalized.bedding.product,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.bedding,
            title: `Подсыпка участка ${normalized.bedding.height} мм`,
            quantity: 1,
            dimensions: {
                width: normalized.bedding.width,
                length: normalized.bedding.length,
                height: normalized.bedding.height,
            },
            pricingInfo: {
                calcMode: 'squareMeter',
                unitValue: pricing.bedding.areaSquareMeters,
                unitPrice: pricing.bedding.unitPrice,
                total: pricing.bedding.total,
            },
            note: 'Имитация до привязки товара',
        })
    }

    if (normalized.foundation.enabled) {
        pushLine({
            ref: normalized.foundation.product,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.foundation,
            title: 'Заливка',
            quantity: 1,
            dimensions: {
                width: normalized.foundation.width,
                length: normalized.foundation.depth,
                height: normalized.foundation.height,
            },
        })
    }

    if (normalized.tile.enabled) {
        pushLine({
            ref: normalized.tile.product,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.tile,
            title: normalized.tile.materialType === 'porcelain'
                ? 'Керамогранитная плитка'
                : 'Гранитная плитка',
            quantity: Math.max(1, pricing.tile.quantity || 1),
            variant: normalized.tile.layout,
            dimensions: {
                width: pricing.tile.pieceWidth,
                length: pricing.tile.pieceLength,
                height: normalized.tile.height,
            },
            pricingInfo: {
                calcMode: 'quantity',
                unitValue: pricing.tile.quantity,
                unitPrice: pricing.tile.unitPrice,
                total: pricing.tile.total,
            },
            note: pricing.tile.layoutLabel,
        })
    }

    if (normalized.pedestal.enabled) {
        pushLine({
            ref: normalized.pedestal.product,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.pedestal,
            title: normalized.pedestal.variant === 'bevel' ? 'Подставка «баян-скос»' : 'Подставка ровная',
            quantity: 1,
            variant: normalized.pedestal.variant,
            dimensions: {
                width: normalized.pedestal.width,
                height: normalized.pedestal.height,
                depth: normalized.pedestal.depth,
            },
            pricingInfo: {
                calcMode: 'quantity',
                unitValue: 1,
                unitPrice: pricing.pedestal.unitPrice,
                total: pricing.pedestal.total,
            },
            note: 'Фиксированная цена',
        })
    }

    if (normalized.plotMode === 'flowerbed') {
        pushLine({
            ref: normalized.flowerbed.longCurbProduct,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb,
            title: 'Поребрик длинный',
            quantity: 2,
            dimensions: {
                length: normalized.flowerbed.length,
                width: normalized.flowerbed.curbWidth,
                height: normalized.flowerbed.curbHeight,
            },
            pricingInfo: {
                calcMode: 'quantity',
                unitValue: pricing.flowerbed.longCurb.quantity,
                unitPrice: pricing.flowerbed.longCurb.unitPrice,
                total: pricing.flowerbed.longCurb.total,
            },
            note: '4 000 ₽ за штуку',
        })
        pushLine({
            ref: normalized.flowerbed.shortCurbProduct,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb,
            title: 'Поребрик короткий',
            quantity: 1,
            dimensions: {
                // Короткий поребрик стоит снаружи перед длинными.
                length: normalized.flowerbed.outerWidth,
                width: normalized.flowerbed.curbWidth,
                height: normalized.flowerbed.curbHeight,
            },
            pricingInfo: {
                calcMode: 'quantity',
                unitValue: pricing.flowerbed.shortCurb.quantity,
                unitPrice: pricing.flowerbed.shortCurb.unitPrice,
                total: pricing.flowerbed.shortCurb.total,
            },
            note: 'Фиксированная цена 2 000 ₽',
        })
    }

    if (normalized.plotMode === 'ledger') {
        pushLine({
            ref: normalized.ledger.product,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.ledger,
            title: normalized.ledger.style === 'shaped' ? 'Надгробная плита фигурная' : 'Надгробная плита',
            quantity: 1,
            variant: normalized.ledger.style,
            dimensions: {
                width: normalized.ledger.width,
                length: normalized.ledger.length,
                height: normalized.ledger.height,
            },
        })
    }

    if (normalized.plotMode === 'grass') {
        pushLine({
            ref: normalized.grass.product,
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.grass,
            title: 'Трава / покрытие',
            quantity: 1,
            dimensions: {
                width: normalized.grass.width,
                length: normalized.grass.length,
                height: normalized.grass.height,
            },
        })
    }

    return lines
}

export const getMonumentAssemblyAccessoryItems = (parentModelId, assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)
    const tileMetrics = getMonumentTileMetrics(normalized, monumentDimensions)
    const items = []
    const add = ({ role, title, quantity = 1, product, material }) => {
        for (let index = 1; index <= quantity; index += 1) {
            items.push({
                sceneId: createMonumentAccessorySceneId(parentModelId, role, index),
                parentModelId: String(parentModelId || ''),
                role,
                title,
                quantity,
                quantityIndex: index,
                product: normalizeProductRef(product, { role, quantity }),
                material,
                pendingCatalogBinding: !product?.productId,
            })
        }
    }

    if (normalized.bedding.enabled) add({
        role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.bedding,
        title: 'Подсыпка участка',
        product: normalized.bedding.product,
        material: normalized.bedding.material,
    })
    if (normalized.foundation.enabled) add({
        role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.foundation,
        title: 'Заливка',
        product: normalized.foundation.product,
        material: normalized.foundation.material,
    })
    if (normalized.tile.enabled) add({
        role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.tile,
        title: normalized.tile.materialType === 'porcelain'
            ? 'Керамогранитная плитка'
            : 'Гранитная плитка',
        quantity: Math.max(1, tileMetrics.quantity || 1),
        product: normalized.tile.product,
        material: normalized.tile.material,
    })
    if (normalized.pedestal.enabled) add({
        role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.pedestal,
        title: normalized.pedestal.variant === 'bevel' ? 'Подставка «баян-скос»' : 'Подставка',
        product: normalized.pedestal.product,
        material: normalized.pedestal.material,
    })
    if (normalized.plotMode === 'flowerbed') {
        add({
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb,
            title: 'Поребрик длинный',
            quantity: 2,
            product: normalized.flowerbed.longCurbProduct,
            material: normalized.flowerbed.longCurbMaterial,
        })
        add({
            role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb,
            title: 'Поребрик короткий',
            product: normalized.flowerbed.shortCurbProduct,
            material: normalized.flowerbed.shortCurbMaterial,
        })
    }
    if (normalized.plotMode === 'ledger') add({
        role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.ledger,
        title: normalized.ledger.style === 'shaped' ? 'Надгробная плита фигурная' : 'Надгробная плита',
        product: normalized.ledger.product,
        material: normalized.ledger.material,
    })
    if (normalized.plotMode === 'grass') add({
        role: MONUMENT_ASSEMBLY_PRODUCT_ROLES.grass,
        title: 'Трава / покрытие',
        product: normalized.grass.product,
        material: normalized.grass.material,
    })

    return items
}

export const getMonumentAssemblyMaterialByRole = (assembly = {}, role = '', monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)

    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.pedestal) return normalized.pedestal.material
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.bedding) return normalized.bedding.material
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.foundation) return normalized.foundation.material
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.tile) return normalized.tile.material
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb) return normalized.flowerbed.longCurbMaterial
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb) return normalized.flowerbed.shortCurbMaterial
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.ledger) return normalized.ledger.material
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.grass) return normalized.grass.material

    return normalizeMaterial(null, 'custom')
}

export const patchMonumentAssemblyMaterialByRole = (assembly = {}, role = '', materialPatch = {}, monumentDimensions = {}) => {
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.pedestal) {
        return patchMonumentAssembly(assembly, { pedestal: { material: materialPatch } }, monumentDimensions)
    }
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.bedding) {
        return patchMonumentAssembly(assembly, { bedding: { material: materialPatch } }, monumentDimensions)
    }
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.foundation) {
        return patchMonumentAssembly(assembly, { foundation: { material: materialPatch } }, monumentDimensions)
    }
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.tile) {
        return patchMonumentAssembly(assembly, { tile: { material: materialPatch } }, monumentDimensions)
    }
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.longCurb) {
        return patchMonumentAssembly(assembly, { flowerbed: { longCurbMaterial: materialPatch } }, monumentDimensions)
    }
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.shortCurb) {
        return patchMonumentAssembly(assembly, { flowerbed: { shortCurbMaterial: materialPatch } }, monumentDimensions)
    }
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.ledger) {
        return patchMonumentAssembly(assembly, { ledger: { material: materialPatch } }, monumentDimensions)
    }
    if (role === MONUMENT_ASSEMBLY_PRODUCT_ROLES.grass) {
        return patchMonumentAssembly(assembly, { grass: { material: materialPatch } }, monumentDimensions)
    }

    return normalizeMonumentAssembly(assembly, monumentDimensions)
}

export const getMonumentAssemblySummary = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)
    const pricing = getMonumentAssemblyKnownPrice(normalized, monumentDimensions)
    const lines = []

    if (normalized.bedding.enabled) {
        lines.push(`подсыпка ${normalized.bedding.width}×${normalized.bedding.length}×${normalized.bedding.height} мм`)
    }
    if (normalized.foundation.enabled) {
        lines.push(`заливка ${normalized.foundation.width}×${normalized.foundation.depth}×${normalized.foundation.height} мм`)
    }
    if (normalized.tile.enabled) {
        lines.push(`${normalized.tile.materialType === 'porcelain' ? 'керамогранит' : 'гранитная плитка'} ${pricing.tile.quantity} шт.`)
    }
    if (normalized.pedestal.enabled) {
        const variant = normalized.pedestal.variant === 'bevel' ? 'баян-скос' : 'ровная'
        lines.push(`подставка ${variant} ${normalized.pedestal.width}×${normalized.pedestal.height}×${normalized.pedestal.depth} мм`)
    }
    if (normalized.plotMode === 'flowerbed') {
        lines.push(`цветник ${normalized.flowerbed.outerWidth}×${normalized.flowerbed.length} мм`)
    }
    if (normalized.plotMode === 'ledger') {
        lines.push(`${normalized.ledger.style === 'shaped' ? 'фигурная надгробка' : 'надгробка'} ${normalized.ledger.width}×${normalized.ledger.length} мм`)
    }
    if (normalized.plotMode === 'grass') {
        lines.push(`трава ${normalized.grass.width}×${normalized.grass.length} мм`)
    }
    if (normalized.upperOffset.x || normalized.upperOffset.y) {
        lines.push(`смещение X ${normalized.upperOffset.x} / Y ${normalized.upperOffset.y} мм`)
    }

    return lines.length ? lines.join(' · ') : 'без основания'
}

export const isMonumentAssemblyEnabled = (assembly = {}, monumentDimensions = {}) => {
    const normalized = normalizeMonumentAssembly(assembly, monumentDimensions)

    return !!(
        normalized.bedding.enabled ||
        normalized.pedestal.enabled ||
        normalized.foundation.enabled ||
        normalized.tile.enabled ||
        normalized.plotMode !== 'none'
    )
}
